"use client";

import { useMemo } from "react";

import {
  encodeSkuKeyDict,
} from "@/shared.utils/products";
import {
  type ProductVariant,
  type RoastedBeansSubscriptionProduct,
  type ShopDetail,
} from "@/stf.data/schema";
import { useVariantState } from "@/stf.m.product/feat.product-detail";

import {
  type ServerDataOutput,
  type SubscriptionVariantKey,
} from "./subscription-data";
import {
  calTotalBeans,
  calTotalShipment,
  extractFrequencyPerMonth,
  extractSubscriptionDuration,
  extractVariantBoxSize,
} from "./utils";

interface UseSubscriptionSelectorActionsProps {
  data: ServerDataOutput;
}
export const useSubscriptionSelectorActions = (props: UseSubscriptionSelectorActionsProps) => {
  const { setVal, opts } = useVariantState();

  const { data } = props;
  const {
    subscriptionData: {
      subscriptionMeta: {
        variantMapper,
      },
    },
    subscriptionVariant,
  } = data;

  const actions = useMemo(() => {
    return {
      setVal: (variantKey: SubscriptionVariantKey, optionKey: string): void => {
        setVal(variantMapper[variantKey], optionKey);
      },
      getVal: (variantKey: SubscriptionVariantKey): string | undefined => {
        return opts?.[variantMapper[variantKey]];
      },
      getOptionName: (variantKey: SubscriptionVariantKey): string | undefined => {
        const variant = subscriptionVariant[`${variantKey}Variant`];
        const selectedOptions = opts?.[variantMapper[variantKey]];
        if (!selectedOptions) {
          return undefined;
        }
        return variant.variantOptions[selectedOptions]?.optionName;
      },
      getOpts: () => {
        return opts;
      },
    };
  }, [setVal, opts, variantMapper, subscriptionVariant]);

  return actions;
};

interface UseSubscriptionSummaryProps {
  data: ServerDataOutput;
}
export const useSubscriptionSummary = (props: UseSubscriptionSummaryProps) => {
  const { data } = props;
  const {
    subscriptionData: {
      variantSkuDict,
      subscriptionMeta: {
        shippingFeePrice,
      },
    },
    stickerPriceData: {
      packageBoxStickerPrice,
    },
  } = data;

  const {
    getOptionName,
    getVal,
    getOpts,
  } = useSubscriptionSelectorActions({ data });

  const opts = getOpts();

  let skuPrice = 0;
  if (opts && Object.keys(opts).length > 0) {
    const selectedSkuKey = encodeSkuKeyDict(opts);
    const selectedSku = variantSkuDict[selectedSkuKey];
    if (selectedSku) {
      skuPrice = selectedSku.retailPrice;
    }
  }

  const selectedPackageKey = getVal("subscriptionPackage");
  const selectedBoxSizeKey = getVal("boxSize");
  const selectedFrequencyKey = getVal("frequencyPerMonth");
  const selectedDurationKey = getVal("subscriptionDuration");

  let productStickerPrice = 0;
  if (selectedPackageKey && selectedBoxSizeKey) {
    productStickerPrice = packageBoxStickerPrice[selectedPackageKey][selectedBoxSizeKey];
  }

  const durationName = getOptionName("subscriptionDuration");
  const frequencyName = getOptionName("frequencyPerMonth");
  const boxGramsName = getOptionName("boxSize");
  const packageName = getOptionName("subscriptionPackage");

  const durationNum = extractSubscriptionDuration(durationName);
  const frequencyNum = extractFrequencyPerMonth(frequencyName);
  const boxGrams = extractVariantBoxSize(boxGramsName);

  const shippingFee = selectedBoxSizeKey ? shippingFeePrice[selectedBoxSizeKey] : 0;

  const totalShipment = calTotalShipment({
    duration: durationNum,
    frequency: frequencyNum,
  });
  const totalBeansGrams = calTotalBeans({
    boxGrams,
    totalShipment,
  });
  const totalShippingFee = totalShipment * shippingFee;
  const totalProductStickerPrice = productStickerPrice * totalShipment;
  const totalBeforeDiscount = totalProductStickerPrice + totalShippingFee;
  const total = skuPrice;

  const discountedPricePerBox = skuPrice / totalShipment;

  const productDiscount = totalProductStickerPrice - total;
  const totalSave = productDiscount + totalShippingFee;
  const totalSavePercent = Math.round((totalSave / (totalProductStickerPrice + totalShippingFee)) * 100);

  return {
    selectedPackageKey,
    selectedBoxSizeKey,
    selectedFrequencyKey,
    selectedDurationKey,

    durationNum,
    frequencyNum,
    boxGrams,

    durationName,
    frequencyName,
    boxGramsName,
    packageName,

    shippingFee,

    totalShipment,
    totalBeansGrams,
    totalShippingFee,
    totalProductStickerPrice,
    total,

    productStickerPrice,
    discountedPricePerBox,

    productDiscount,
    totalSave,
    totalSavePercent,
    totalBeforeDiscount,
  };
};
